import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';
import { refreshNow } from '@/auth/refreshLoop';

/*
 * Axios client — single instance, single interceptor.
 *
 * - Base URL is '/api' (Vite dev proxy forwards to Express on :3000).
 * - JWT is attached from the auth store on every request.
 * - 401 responses trigger one refresh attempt (cognito mode only) before
 *   logging the user out. The retry flag prevents the refreshed request
 *   from looping on a second 401.
 * - Offline requests fail fast with a synthetic NetworkError so the user
 *   isn't waiting 15s for a timeout when their device is plainly offline.
 */

export const api = axios.create({
    baseURL: '/api',
    // 15s — long enough for a slow connection on a slow endpoint, short
    // enough that a stuck request surfaces as an error before the user
    // has clicked the button a second time.
    timeout: 15_000,
});

/** Thrown by the offline guard so callers can distinguish offline from a
 *  server error and render appropriate feedback. */
export class OfflineError extends Error {
    isOffline = true as const;
    constructor() {
        super('You appear to be offline. Check your connection and try again.');
        this.name = 'OfflineError';
    }
}

interface RetryableConfig extends InternalAxiosRequestConfig {
    /** Internal flag — true once a request has been retried after a refresh. */
    _gsRefreshRetried?: boolean;
}

api.interceptors.request.use((config) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return Promise.reject(new OfflineError());
    }
    const token = useAuthStore.getState().token;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (res) => res,
    async (err: AxiosError<{ error?: string }>) => {
        const status = err.response?.status;
        const original = err.config as RetryableConfig | undefined;

        if (status === 401 && original && !original._gsRefreshRetried) {
            const fresh = await refreshNow();
            if (fresh) {
                original._gsRefreshRetried = true;
                original.headers = original.headers ?? {};
                original.headers.Authorization = `Bearer ${fresh}`;
                return api.request(original);
            }
            useAuthStore.getState().logout();
        } else if (status === 401) {
            useAuthStore.getState().logout();
        }
        return Promise.reject(err);
    },
);
